import { useEffect, useRef, useState, useMemo } from 'react';
import { CalendarDays, CircleUserRound, Clock3, Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PlacesAutocompleteInput } from '../inputs/PlacesAutocompleteInput';
import { useGoogleMapsScript } from '../../hooks/useGoogleMapsScript';

const defaultCenter = { lat: 41.015137, lng: 28.97953 };

const timingOptions = [
  { label: 'Pick up now', value: 'now' },
  { label: 'Schedule pickup', value: 'schedule' },
];

const tripForOptions = [
  { label: 'For me', value: 'me' },
  { label: 'For someone else', value: 'other' },
];

export function RoutePlanner({
  title,
  subtitle,
  ctaLabel,
  onSearchChange,
  initialFilters = {
    from: { description: '' },
    to: { description: '' },
    departureDate: '',
  },
}) {
  const extractSelection = filter => {
    if (
      filter?.location &&
      typeof filter.location.latitude === 'number' &&
      typeof filter.location.longitude === 'number'
    ) {
      return {
        description: filter.description || '',
        latitude: filter.location.latitude,
        longitude: filter.location.longitude,
        placeId: filter.placeId,
      };
    }
    return null;
  };

  const [fromLocation, setFromLocation] = useState(
    extractSelection(initialFilters?.from),
  );
  const [toLocation, setToLocation] = useState(
    extractSelection(initialFilters?.to),
  );
  const [fromDescription, setFromDescription] = useState(
    initialFilters?.from?.description || '',
  );
  const [toDescription, setToDescription] = useState(
    initialFilters?.to?.description || '',
  );
  const [departureDate, setDepartureDate] = useState(
    initialFilters?.departureDate || '',
  );
  const [mapError, setMapError] = useState('');

  const { t } = useTranslation();

  const { ready } = useGoogleMapsScript();
  const mapContainerRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markersRef = useRef([]);

  useEffect(() => {
    setFromLocation(extractSelection(initialFilters?.from));
    setFromDescription(initialFilters?.from?.description || '');
    setToLocation(extractSelection(initialFilters?.to));
    setToDescription(initialFilters?.to?.description || '');
    setDepartureDate(initialFilters?.departureDate || '');
  }, [initialFilters]);

  const isActionDisabled = useMemo(() => {
    const hasFrom = Boolean(fromDescription?.trim());
    const hasTo = Boolean(toDescription?.trim());
    return !hasFrom || !hasTo;
  }, [fromDescription, toDescription]);

  const emitFilters = () => {
    onSearchChange?.({
      from: {
        description: fromDescription.trim(),
        location: fromLocation
          ? {
              latitude: fromLocation.latitude,
              longitude: fromLocation.longitude,
            }
          : null,
      },
      to: {
        description: toDescription.trim(),
        location: toLocation
          ? {
              latitude: toLocation.latitude,
              longitude: toLocation.longitude,
            }
          : null,
      },
      departureDate,
    });
  };

  useEffect(() => {
    if (!ready || !mapContainerRef.current || mapInstanceRef.current) return;

    mapInstanceRef.current = new window.google.maps.Map(
      mapContainerRef.current,
      {
        center: defaultCenter,
        zoom: 10,
        disableDefaultUI: true,
        styles: [
          {
            featureType: 'poi',
            stylers: [{ visibility: 'off' }],
          },
        ],
      },
    );

    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      map: mapInstanceRef.current,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#111827',
        strokeWeight: 5,
      },
    });
  }, [ready]);

  // Show markers when locations are selected (even without route)
  useEffect(() => {
    if (!ready || !mapInstanceRef.current) return;

    clearMarkers();

    if (fromLocation) {
      const marker = new window.google.maps.Marker({
        position: {
          lat: fromLocation.latitude,
          lng: fromLocation.longitude,
        },
        map: mapInstanceRef.current,
        label: {
          text: 'A',
          color: '#111827',
          fontSize: '14px',
          fontWeight: '700',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#111827',
          fillOpacity: 1,
          strokeWeight: 3,
          strokeColor: '#ffffff',
        },
      });
      markersRef.current.push(marker);
    }

    if (toLocation) {
      const marker = new window.google.maps.Marker({
        position: {
          lat: toLocation.latitude,
          lng: toLocation.longitude,
        },
        map: mapInstanceRef.current,
        label: {
          text: 'B',
          color: '#111827',
          fontSize: '14px',
          fontWeight: '700',
        },
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 12,
          fillColor: '#facc15',
          fillOpacity: 1,
          strokeWeight: 3,
          strokeColor: '#ffffff',
        },
      });
      markersRef.current.push(marker);
    }

    // Center map on markers if both are present
    if (fromLocation && toLocation) {
      const bounds = new window.google.maps.LatLngBounds();
      bounds.extend({
        lat: fromLocation.latitude,
        lng: fromLocation.longitude,
      });
      bounds.extend({
        lat: toLocation.latitude,
        lng: toLocation.longitude,
      });
      mapInstanceRef.current.fitBounds(bounds);
    } else if (fromLocation) {
      mapInstanceRef.current.setCenter({
        lat: fromLocation.latitude,
        lng: fromLocation.longitude,
      });
      mapInstanceRef.current.setZoom(12);
    } else if (toLocation) {
      mapInstanceRef.current.setCenter({
        lat: toLocation.latitude,
        lng: toLocation.longitude,
      });
      mapInstanceRef.current.setZoom(12);
    }
  }, [fromLocation, toLocation, ready]);

  // Draw route when both locations are selected
  useEffect(() => {
    if (!ready || !fromLocation || !toLocation) {
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setDirections({ routes: [] });
      }
      return;
    }

    const directionsService = new window.google.maps.DirectionsService();
    directionsService.route(
      {
        origin: {
          lat: fromLocation.latitude,
          lng: fromLocation.longitude,
        },
        destination: {
          lat: toLocation.latitude,
          lng: toLocation.longitude,
        },
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === 'OK') {
          setMapError('');
          directionsRendererRef.current?.setDirections(result);
        } else {
          setMapError('Unable to draw route. Try different points.');
        }
      },
    );
  }, [fromLocation, toLocation, ready]);

  const clearMarkers = () => {
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];
  };

  return (
    <section className="mt-10 grid gap-6 sm:gap-8 lg:grid-cols-[340px,1fr] xl:grid-cols-[360px,1fr]">
      <div className="relative z-20 bg-white dark:bg-slate-900 p-5 sm:p-6 shadow-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.6em] text-yellow-500">
          {title}
        </p>
        <h2 className="mt-2 text-2xl font-black text-gray-900 dark:text-white">
          {subtitle}
        </h2>
        <div className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
              {t('home.from')}
            </label>
            <PlacesAutocompleteInput
              placeholder={t('routePlanner.fromPlaceholder')}
              value={
                fromLocation ||
                (fromDescription ? { description: fromDescription } : null)
              }
              onSelect={selection => {
                setFromLocation(selection);
                setFromDescription(selection?.description || '');
              }}
              onInputChange={value => setFromDescription(value)}
              icon={
                <Search className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              }
              disabled={!ready}
            />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
              {t('home.to')}
            </label>
            <PlacesAutocompleteInput
              placeholder={t('routePlanner.toPlaceholder')}
              value={
                toLocation ||
                (toDescription ? { description: toDescription } : null)
              }
              onSelect={selection => {
                setToLocation(selection);
                setToDescription(selection?.description || '');
              }}
              onInputChange={value => setToDescription(value)}
              icon={
                <Search className="h-4 w-4 text-gray-500 dark:text-slate-400" />
              }
              disabled={!ready}
            />
          </div>
          <label className="text-xs font-semibold uppercase tracking-[0.3em] text-gray-500 dark:text-slate-400">
            {t('requestDelivery.date')}
          </label>
          <div className="relative mt-2">
            <CalendarDays className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500 dark:text-slate-400" />
            <input
              type="date"
              value={departureDate}
              onChange={event => setDepartureDate(event.target.value)}
              className="w-full rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-800 py-3 pl-12 pr-4 text-sm text-gray-900 dark:text-white focus:border-black dark:focus:border-yellow-500 focus:outline-none"
            />
          </div>
        </div>
        <button
          type="button"
          disabled={isActionDisabled}
          onClick={emitFilters}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-2xl bg-black dark:bg-slate-700 py-3 text-sm font-semibold text-yellow-400 transition hover:bg-gray-900 dark:hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-gray-300 dark:disabled:bg-slate-700 disabled:text-gray-500 dark:disabled:text-slate-500"
        >
          {ctaLabel}
        </button>
        {mapError && (
          <p className="mt-3 text-xs text-red-500 dark:text-red-400">
            {mapError}
          </p>
        )}
      </div>

      <div className="w-full">
        {ready ? (
          <div
            ref={mapContainerRef}
            className="h-[580px] w-full  overflow-hidden"
          />
        ) : (
          <div className="flex h-[580px] items-center justify-center text-gray-500 dark:text-slate-400">
            {t('routePlanner.loadingMaps')}
          </div>
        )}
      </div>
    </section>
  );
}
